import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useIntl} from 'react-intl';
import MuiMenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import {IoMdGlobe} from '@react-icons/all-files/io/IoMdGlobe';
import {LOCALES} from '@constants/main';
import {loadTranslations} from '@components/appSlice';
import {
    selectRecipientLanguage,
    selectMergedTransactionSettings,
    updateRecipientLanguage
} from '@components/SignerUi/signerUiSlice';

const MENU_ID = 'language-menu';
const ITEM_HEIGHT_PX = 46;
const MAX_VISIBLE_ITEMS = 9;

export default function LanguageSelector({shouldShowIcon = false, isMenuOpen = false}) {
    const intl = useIntl();
    const dispatch = useDispatch();
    const currentLanguage = useSelector(selectRecipientLanguage);
    const mergedTransactionSettings = useSelector(selectMergedTransactionSettings);
    const [anchorEl, setAnchorEl] = useState(null);
    const isLanguageMenuOpen = !!anchorEl;

    const hideLanguageDropdown = mergedTransactionSettings?.hide_language_dropdown;
    const isHidden = hideLanguageDropdown === true || hideLanguageDropdown === 'true';
    const languageLabel = intl.formatMessage({id: `esl.language.${currentLanguage}`, defaultMessage: ''});

    function handleMenuOpen(event) {
        setAnchorEl(event.currentTarget);
    }

    function handleMenuClose() {
        setAnchorEl(null);
    }

    function handleLanguageChange(locale) {
        return async () => {
            const result = await dispatch(updateRecipientLanguage({language: locale}));

            if (updateRecipientLanguage.fulfilled.match(result)) {
                dispatch(loadTranslations(locale));
            }

            handleMenuClose();
        };
    }

    if (isHidden) return null;

    return (
        <>
            <div className="signing-menu-item" data-wdio="language-selector">
                <button
                    type="button"
                    className="signing-menu-item-btn"
                    onClick={handleMenuOpen}
                    tabIndex={isMenuOpen ? 0 : -1}
                >
                    <span className="signing-menu-item-icon">
                        <IoMdGlobe className="icon" aria-hidden />
                    </span>
                    {isMenuOpen && (
                        <span className="signing-menu-item-label">{languageLabel}</span>
                    )}
                </button>
            </div>
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                id={MENU_ID}
                keepMounted
                open={isLanguageMenuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                    style: {
                        maxHeight: ITEM_HEIGHT_PX * MAX_VISIBLE_ITEMS
                    }
                }}
                MenuListProps={{
                    disablePadding: true,
                    classes: {root: 'language-menu-list'}
                }}
            >
                {LOCALES.map((locale) => (
                    <MuiMenuItem
                        onClick={handleLanguageChange(locale)}
                        selected={locale === currentLanguage}
                        key={locale}
                        data-wdio={`language-option-${locale}`}
                    >
                        {intl.formatMessage({id: `esl.language.${locale}`, defaultMessage: ''})}
                    </MuiMenuItem>
                ))}
            </Menu>
        </>
    );
}
